import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../services/api';
import { getSwalTheme } from '../utils/swalTheme';
import { VEHICLE_YEARS, DRIVE_TYPES } from '../constants/vehicles';
import './PostShiftPage.css';

const PostShiftPage = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    shift_type: '',
    pay_rate: '',
    pay_type: 'daily',
    drive_type: 'standard',
    wav: false,
    garage_address: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Vehicle entry state
  const [vehicle, setVehicle] = useState({
    vehicle_type: 'yellow_cab',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    medallion_number: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Auto-set pay_type based on shift_type
    if (name === 'shift_type') {
      const payType = (value === 'dov' || value.startsWith('weekly_')) ? 'weekly' : 'daily';
      setFormData(prev => ({ ...prev, [name]: value, pay_type: payType }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleVehicleChange = (e) => {
    const { name, value } = e.target;
    setVehicle(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate vehicle fields
    if (!vehicle.vehicle_type) {
      setError('Please select a work type.');
      return;
    }
    if (!vehicle.make.trim()) {
      setError('Please enter the vehicle make.');
      return;
    }
    if (!vehicle.model.trim()) {
      setError('Please enter the vehicle model.');
      return;
    }

    setLoading(true);

    try {
      const shiftData = {
        ...formData,
        pay_rate: formData.pay_rate ? parseFloat(formData.pay_rate) : null,
        vehicle_type: vehicle.vehicle_type,
        drive_type: formData.drive_type,
        wav: formData.wav,
        vehicle_make: vehicle.make.trim(),
        vehicle_model: vehicle.model.trim(),
        vehicle_year: parseInt(vehicle.year),
        medallion_number: vehicle.medallion_number || ''
      };

      await api.post('/shifts', shiftData);
      await Swal.fire({
        ...getSwalTheme(),
        icon: 'success',
        title: 'Car Listing Posted!',
        text: 'Your listing is now visible to drivers.'
      });
      navigate('/dashboard');
    } catch (err) {
      Swal.fire({
        ...getSwalTheme(),
        icon: 'error',
        title: 'Post Failed',
        text: err.response?.data?.error || err.message || 'Failed to create listing'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="post-shift-page">
      <div className="post-shift-container">
        <h1>Car Listing</h1>
        <p className="subtitle">Find a driver for your vehicle</p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="post-shift-form">
          <div className="form-group">
            <label htmlFor="title">Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., Yellow Cab available for weekly day shift"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="e.g., Clean vehicle, E-ZPass included, gas not included..."
              rows={2}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="vehicle_type">Work Type *</label>
              <select
                id="vehicle_type"
                name="vehicle_type"
                value={vehicle.vehicle_type}
                onChange={handleVehicleChange}
                required
              >
                <option value="yellow_cab">Yellow Cab</option>
                <option value="green_boro">Green Boro</option>
                <option value="black_car">Black Car</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="drive_type">Drive Type *</label>
              <select
                id="drive_type"
                name="drive_type"
                value={formData.drive_type}
                onChange={handleChange}
              >
                {DRIVE_TYPES.map(dt => (
                  <option key={dt.value} value={dt.value}>{dt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <div className="checkbox-group" style={{ display: 'flex' }}>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="wav"
                  checked={formData.wav}
                  onChange={handleCheckboxChange}
                />
                WAV
              </label>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="make">Make *</label>
              <input
                type="text"
                id="make"
                name="make"
                value={vehicle.make}
                onChange={handleVehicleChange}
                placeholder="e.g., Toyota"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="model">Model *</label>
              <input
                type="text"
                id="model"
                name="model"
                value={vehicle.model}
                onChange={handleVehicleChange}
                placeholder="e.g., Camry"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="year">Year *</label>
              <select
                id="year"
                name="year"
                value={vehicle.year}
                onChange={handleVehicleChange}
              >
                {VEHICLE_YEARS.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="medallion_number">Medallion #</label>
              <input
                type="text"
                id="medallion_number"
                name="medallion_number"
                value={vehicle.medallion_number}
                onChange={handleVehicleChange}
                placeholder="e.g., 1A23"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="shift_type">Shift Type *</label>
              <select
                id="shift_type"
                name="shift_type"
                value={formData.shift_type}
                onChange={handleChange}
                required
              >
                <option value="">Select shift type</option>
                <option value="dov">DOV (Drive Own Vehicle)</option>
                <option value="weekly_24">Weekly 24hr</option>
                <option value="weekly_day">Weekly Day</option>
                <option value="weekly_night">Weekly Night</option>
                <option value="daily_day">Daily Day</option>
                <option value="daily_night">Daily Night</option>
                <option value="daily_24">Daily 24hr</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="pay_rate">
                Rental Rate {formData.pay_type === 'weekly' ? '/ week' : '/ day'}
              </label>
              <div className="rate-input-wrapper">
                <span className="rate-prefix">$</span>
                <input
                  type="number"
                  id="pay_rate"
                  name="pay_rate"
                  value={formData.pay_rate}
                  onChange={handleChange}
                  placeholder="e.g., 125"
                  min="0"
                  step="0.01"
                  className="rate-input"
                />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="garage_address">Location</label>
            <input
              type="text"
              id="garage_address"
              name="garage_address"
              value={formData.garage_address}
              onChange={handleChange}
              placeholder="e.g., LIC, Queens"
            />
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={() => navigate('/dashboard')}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={loading}
            >
              {loading ? 'Posting...' : 'Post Car Listing'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PostShiftPage;
